import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Gift, 
  Copy, 
  Check, 
  Users, 
  Crown,
  Share2,
  Twitter
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReferralPage() {
  const { user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [inputCode, setInputCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const fetchReferralData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API_URL}/api/referral/code`, { withCredentials: true });
        setReferralData(data);
      } catch (err) {
        toast.error('Failed to load referral data');
      } finally {
        setLoading(false);
      }
    };
    fetchReferralData();
  }, [user]);

  const copyCode = async () => {
    if (referralData?.code) {
      await navigator.clipboard.writeText(referralData.code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/register?ref=${referralData?.code}`;
    await navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const applyCode = async () => {
    if (!inputCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/referral/apply?code=${inputCode.trim()}`,
        {},
        { withCredentials: true }
      );
      toast.success(data.message);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply referral code');
    } finally {
      setApplying(false);
    }
  };

  const shareToTwitter = () => {
    const text = `Join me on Sudoku Academy - the ultimate puzzle platform with 17 difficulty levels!\n\nUse my code ${referralData?.code} for a FREE week of Premium!\n\n#SudokuAcademy #Sudoku`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-[#8B5A3C]/30 border-t-[#8B5A3C] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#34C759]/10 border border-[#34C759]/20 mb-4">
            <Gift className="w-4 h-4 text-[#34C759]" />
            <span className="text-sm text-[#34C759] font-medium">Give a Week, Get a Week</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-2">
            Invite Friends
          </h1>
          <p className="text-[var(--text-secondary)] text-lg">
            Share your code and you both get 1 week of Premium free!
          </p>
        </div>

        {user ? (
          <div className="space-y-6">
            {/* Your Referral Code */}
            <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
              <CardHeader>
                <CardTitle className="text-[var(--text-primary)] font-heading flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-[#8B5A3C]" />
                  Your Referral Code
                </CardTitle>
                <CardDescription className="text-[var(--text-secondary)]">
                  Share this code with friends
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-4">
                  <div className="flex-1 bg-[var(--background-elevated)] border border-[var(--border-subtle)] rounded px-4 py-3 font-mono text-xl text-[var(--text-primary)] text-center">
                    {referralData?.code || 'Loading...'}
                  </div>
                  <Button
                    onClick={copyCode}
                    className="bg-[#8B5A3C] hover:bg-[#A46944] text-white"
                    data-testid="copy-code-btn"
                  >
                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={copyLink}
                    className="flex-1 border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--hover-bg)]"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button
                    variant="outline"
                    onClick={shareToTwitter}
                    className="flex-1 border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--hover-bg)]"
                  >
                    <Twitter className="w-4 h-4 mr-2" />
                    Share on X
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
                <CardContent className="p-6 text-center">
                  <Users className="w-8 h-8 text-[#8B5A3C] mx-auto mb-2" />
                  <div className="text-3xl font-bold text-[var(--text-primary)] font-mono">
                    {referralData?.referrals || 0}
                  </div>
                  <div className="text-sm text-[var(--text-secondary)]">Friends Invited</div>
                </CardContent>
              </Card>
              <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
                <CardContent className="p-6 text-center">
                  <Crown className="w-8 h-8 text-[#FF9F0A] mx-auto mb-2" />
                  <div className="text-3xl font-bold text-[var(--text-primary)] font-mono">
                    {referralData?.rewards_earned || 0}
                  </div>
                  <div className="text-sm text-[var(--text-secondary)]">Weeks Earned</div>
                </CardContent>
              </Card>
            </div>

            {/* Apply Code */}
            {!user.referred_by && (
              <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
                <CardHeader>
                  <CardTitle className="text-[var(--text-primary)] font-heading">Have a Referral Code?</CardTitle>
                  <CardDescription className="text-[var(--text-secondary)]">
                    Enter a friend's code to get 1 week of Premium free
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <Input
                      value={inputCode}
                      onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                      placeholder="Enter code (e.g., SUDOKU1234)"
                      className="flex-1 bg-[var(--background-elevated)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-disabled)] font-mono"
                      data-testid="referral-input"
                    />
                    <Button
                      onClick={applyCode}
                      disabled={applying}
                      className="bg-[#34C759] hover:bg-[#2DB84D] text-white"
                      data-testid="apply-code-btn"
                    >
                      {applying ? 'Applying...' : 'Apply'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* How it Works */}
            <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
              <CardHeader>
                <CardTitle className="text-[var(--text-primary)] font-heading">How It Works</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="text-center p-4">
                    <div className="w-10 h-10 rounded-full bg-[#8B5A3C]/20 flex items-center justify-center mx-auto mb-3">
                      <span className="text-[#8B5A3C] font-bold">1</span>
                    </div>
                    <p className="text-[var(--text-primary)] font-medium mb-1">Share Your Code</p>
                    <p className="text-sm text-[var(--text-secondary)]">Send your unique code to friends</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="w-10 h-10 rounded-full bg-[#8B5A3C]/20 flex items-center justify-center mx-auto mb-3">
                      <span className="text-[#8B5A3C] font-bold">2</span>
                    </div>
                    <p className="text-[var(--text-primary)] font-medium mb-1">They Sign Up</p>
                    <p className="text-sm text-[var(--text-secondary)]">Friend enters code after registration</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="w-10 h-10 rounded-full bg-[#34C759]/20 flex items-center justify-center mx-auto mb-3">
                      <span className="text-[#34C759] font-bold">3</span>
                    </div>
                    <p className="text-[var(--text-primary)] font-medium mb-1">Both Get Rewarded</p>
                    <p className="text-sm text-[var(--text-secondary)]">You both get 1 week Premium free!</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] text-center p-8">
            <Gift className="w-12 h-12 text-[#8B5A3C] mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-[var(--text-primary)] mb-2">
              Sign In to Get Your Code
            </h3>
            <p className="text-[var(--text-secondary)] mb-4">
              Create an account to start inviting friends and earning free Premium weeks.
            </p>
            <Button
              onClick={() => window.location.href = '/register'}
              className="bg-[#8B5A3C] hover:bg-[#A46944] text-white"
            >
              Get Started
            </Button>
          </Card>
        )}
      </main>
    </div>
  );
}
